import InlineTrigger from '../../components/triggers/InlineTrigger';
import { useTriggers } from '../../contexts/TriggerContext';
// frontend/src/pages/Home/FollowingFeed.js
import '../../pages/Create/CreateContent.css';
import { useNavigate } from 'react-router-dom';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import PostDisplay from '../../components/PostDisplay';
import QuestionDisplay from '../../components/QuestionDisplay';
import WorkedExampleDisplay from '../../components/WorkedExampleDisplay';
import FilterPanel from '../../components/FilterPanel';
import './FollowingFeed.css';

// Define the backend API URL
const API_URL = '';

const FollowingFeed = () => {
  const triggerCtx = useTriggers();
  const inlineTriggers = triggerCtx ? triggerCtx.getTriggersByPlacement('following_feed') : [];
  const navigate = useNavigate();
  const { user } = useAuth();
  const userGrade = user?.profile?.grade || 10;
  const [content, setContent] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [focusMode, setFocusMode] = useState(false);
  const [filters, setFilters] = useState({
    grade: userGrade,
    topic: (userGrade >= 10) ? 'Algebra' : 'Integers',
    subtopic: ''
  });
  const [showFilterPanel, setShowFilterPanel] = useState(false);

  // Reset filters when user changes grade
  useEffect(() => {
    const newGrade = user?.profile?.grade;
    if (newGrade && newGrade !== filters.grade) {
      setFilters({
        grade: newGrade,
        topic: (newGrade >= 10) ? 'Algebra' : 'Integers',
        subtopic: ''
      });
      setContent([]);
      setPage(1);
      setHasMore(true);
    }
  }, [user?.profile?.grade]);
  const [message, setMessage] = useState('');
  const observer = useRef();

  // Infinite scroll callback
  const lastContentElementRef = useCallback(node => {
    if (loading) return;
    if (observer.current) observer.current.disconnect();

    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore) {
        setPage(prevPage => prevPage + 1);
      }
    });

    if (node) observer.current.observe(node);
  }, [loading, hasMore]);

  // Fetch content
  const fetchContent = useCallback(async (resetContent = false) => {
    if (loading) return;

    setLoading(true);
    try {
      const params = {
        page: resetContent ? 1 : page,
        limit: 10,
        focusMode: focusMode,
        ...filters
      };

      // Use the correct backend URL
      const response = await axios.get(`${API_URL}/api/feed/following`, {
        params,
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (resetContent) {
        setContent(response.data.content || []);
        setPage(1);
      } else {
        setContent(prev => [...prev, ...(response.data.content || [])]);
      }

      setHasMore(response.data.hasMore);
      setMessage(response.data.message || '');

    } catch (error) {
      console.error('Error fetching following feed:', error);
      setMessage('Failed to load feed');
    } finally {
      setLoading(false);
    }
  }, [page, focusMode, filters, loading]);

  // Initial load and when filters change
  useEffect(() => {
    fetchContent(true);
  }, [focusMode, filters.grade, filters.topic, filters.subtopic]);

  // Load more on page change
  useEffect(() => {
    if (page > 1) {
      fetchContent();
    }
  }, [page]);

  // Toggle focus mode
  const handleToggleFocusMode = async () => {
    const newMode = !focusMode;
    setFocusMode(newMode);
    setPage(1);
    setContent([]);

    // Save preference to backend with correct URL
    try {
      await axios.put(`${API_URL}/api/users/learning-preferences`, {
        focusMode: newMode
      }, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });
    } catch (error) {
      console.error('Error saving focus mode preference:', error);
    }
  };

  // Apply filters
  const handleApplyFilters = async (newFilters) => {
    setFilters(newFilters);
    setShowFilterPanel(false);
    setPage(1);
    setContent([]);

    // Save preferences to backend with correct URL
    try {
      await axios.put(`${API_URL}/api/users/learning-preferences`, {
        focusGrade: newFilters.grade,
        focusTopic: newFilters.topic,
        focusSubtopic: newFilters.subtopic
      }, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });
    } catch (error) {
      console.error('Error saving filter preferences:', error);
    }
  };

  // Render content item
  const renderContentItem = (item, index) => {
    if (!item || !item.data) return null;

    const isLastElement = content.length === index + 1;
    const ref = isLastElement ? lastContentElementRef : null;

    return (
      <div ref={ref} key={`${item.type}-${item.data._id || index}`} className="feed-item">
        {item.type === 'post' && (
          <PostDisplay post={item.data} />
        )}
        {item.type === 'question' && (
          <QuestionDisplay question={item.data} />
        )}
        {item.type === 'workedExample' && (
          <WorkedExampleDisplay example={item.data} />
        )}
      </div>
    );
  };

  return (
    <div className="following-feed">
      <div className="feed-header">
        <div className="feed-mode-toggle">
          <button
            className={`mode-btn ${!focusMode ? 'active' : ''}`}
            onClick={() => focusMode && handleToggleFocusMode()}
          >
            <i className="fas fa-users"></i>
            Following
          </button>
          <button
            className={`mode-btn ${focusMode ? 'active' : ''}`}
            onClick={() => !focusMode && handleToggleFocusMode()}
          >
            <i className="fas fa-book"></i>
            Focus Study
          </button>
        </div>

        {focusMode && (
          <div className="filter-controls">
            <div className="current-filters">
              <span className="filter-badge">Grade {filters.grade}</span>
              <span className="filter-badge">{filters.topic}</span>
              {filters.subtopic && (
                <span className="filter-badge">{filters.subtopic}</span>
              )}
            </div>
            <button
              className="filter-btn"
              onClick={() => setShowFilterPanel(!showFilterPanel)}
            >
              <i className="fas fa-filter"></i>
              Filter
            </button>
          </div>
        )}
      </div>

      {showFilterPanel && (
        <FilterPanel
          currentFilters={filters}
          onApply={handleApplyFilters}
          onClose={() => setShowFilterPanel(false)}
        />
      )}

      <div className="feed-content">
        {content.length === 0 && !loading && (
          <div className="empty-feed">
            {focusMode ? (
              <>
                <i className="fas fa-book-open"></i>
                <h3>No content found</h3>
                <p>Try adjusting your filters to see more content</p>
              </>
            ) : (
              <>
                <i className="fas fa-user-friends"></i>
                <h3>{message || 'Your following feed is empty'}</h3>
                <p>Follow other users to see their posts here!</p>
              </>
            )}
          </div>
        )}

        {content.map((item, index) => renderContentItem(item, index))}

        {loading && (
          <div className="loading-spinner">
            <i className="fas fa-spinner fa-spin"></i>
          </div>
        )}
      </div>
    
      <button className="create-fab" onClick={() => navigate('/create')} title="Create content">
        <i className="fas fa-plus"></i>
      </button>
    </div>
  );
};

export default FollowingFeed;
